<template>
  <div class="video-source">
    <!-- Header Section -->
    <div class="video-source__header">
      <div>
        <h3 class="video-source__title">
          Video Assets
        </h3>
        <span class="video-source__description">
          You can only select up to 4 videos
        </span>
      </div>
      <div class="video-source__actions">
        <b-button
          v-if="hasSelectedVideos"
          class="clear-selection-button"
          variant="outline-secondary"
          size="sm"
          @click="clearSelection"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
          Clear selection
        </b-button>
        <b-button
          class="next-button"
          variant="primary"
          pill
          :disabled="!hasSelectedVideos"
          @click="handleNext"
        >
          Next
          <feather-icon
            icon="ArrowRightIcon"
            size="16"
          />
        </b-button>
      </div>
    </div>

    <!-- Video Grid -->
    <div class="waterfall-container">
      <!-- Add More Videos Card - Always First -->
      <div class="waterfall-column">
        <AddVideoCard
          @video-uploaded="handleVideoUploaded"
          @videos-selected="handleVideosSelected"
        />
      </div>

      <div
        v-for="video in videoSource"
        :key="video.id"
        class="waterfall-column"
      >
        <VideoCard
          :video-id="video.id"
          :video-url="video.videoUrl"
          :thumbnail-url="video.imageUrl"
          :is-selected="isVideoSelected(video.id)"
          :is-loading="video.isPolling"
          :selection-order="getSelectionOrder(video.id)"
          selection-type="checkbox"
          @select="handleVideoSelect"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import AddVideoCard from './AddVideoCard.vue'
import VideoCard from './VideoCard.vue'

/**
 * VideoSource Component
 * Displays generated videos in a waterfall layout with selection and playback controls
 */
export default {
  name: 'VideoSource',
  components: {
    BButton,
    AddVideoCard,
    VideoCard,
  },
  data() {
    return {
      videoSource: [],
      selectedVideoIds: [],
    }
  },
  computed: {
    hasSelectedVideos() {
      return this.selectedVideoIds.length > 0
    },
    maxSelectionReached() {
      return this.selectedVideoIds.length >= 4
    },
  },
  beforeDestroy() {
    this.cleanupPollingTimers()
  },
  methods: {
    ...mapActions('videoGeneration', ['pollVideoStatus', 'addSelectedVideo']),

    /**
     * Add video to the list and start polling if needed
     * @param {Object} video - Video object with id, taskId, status, etc.
     * @param {Object} options - Additional options
     * @param {boolean} options.autoSelect - Whether to auto-select the video
     */
    addVideo(video, options = {}) {
      if (!video || !video.id) return

      // Check if video already exists to prevent duplicates
      const existingIndex = this.videoSource.findIndex(v => v.id === video.id)
      if (existingIndex >= 0) {
        return
      }

      const videoCard = {
        ...video,
        isPolling: !video.videoUrl && video.status !== 'SUCCESS' && video.status !== 'FAILED',
        timerId: null,
      }

      this.videoSource = [videoCard, ...this.videoSource]

      if (videoCard.isPolling) {
        this.startPolling(videoCard)
      }

      // Auto-select if requested (for uploaded and library videos)
      if (options.autoSelect) {
        this.selectedVideoIds.push(video.id)
      }
    },

    /**
     * Start polling for video status
     * @param {Object} video - Video object to poll
     */
    startPolling(video) {
      if (!video.taskId) return

      // Check if already polling to prevent duplicate timers
      if (video.timerId) {
        return
      }

      const poll = async () => {
        try {
          const response = await this.pollVideoStatus({ taskIds: [video.taskId] })
          const taskResult = response?.data || response

          if (taskResult && taskResult.length > 0) {
            const result = taskResult.find(item => item[0].task_id === video.taskId)
            if (result) {
              this.updateVideoFromPolling(video, result[0])
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Polling failed for video:', video.id, error)
        }
      }

      const timer = setInterval(poll, 5000)
      this.$set(video, 'timerId', timer)
      poll() // Initial poll
    },

    /**
     * Update video from polling result
     * @param {Object} video - Video object to update
     * @param {Object} taskResult - Result from polling
     */
    updateVideoFromPolling(video, taskResult) {
      const status = taskResult.status || 'FAILED'
      const videoUrl = taskResult.preview_url || ''

      // Stop polling for completed or failed videos
      if (status === 'SUCCESS' || status === 'FAILED') {
        this.stopPolling(video)

        if (status === 'FAILED') {
          this.removeVideoFromList(video.id)
          this.$toast.error('Video generation failed')

          // Emit event to reset generating state in image card
          this.$emit('video-polling-complete', {
            taskId: video.taskId,
            status,
            videoUrl: '',
          })
          return
        }

        // Update successful video
        this.$set(video, 'status', status)
        this.$set(video, 'videoUrl', videoUrl)
        this.$set(video, 'isPolling', false)
        this.$toast.success('Video generation completed')

        // Emit event to reset generating state in image card
        this.$emit('video-polling-complete', {
          taskId: video.taskId,
          status,
          videoUrl,
        })
      }
    },

    /**
     * Stop polling for a video
     * @param {Object} video - Video object
     */
    stopPolling(video) {
      if (video.timerId) {
        clearInterval(video.timerId)
        this.$set(video, 'timerId', null)
      }
      this.$set(video, 'isPolling', false)
    },

    /**
     * Remove video from list
     * @param {string} videoId - Video ID to remove
     */
    removeVideoFromList(videoId) {
      const index = this.videoSource.findIndex(video => video.id === videoId)
      if (index > -1) {
        const video = this.videoSource[index]
        if (video.timerId) {
          clearInterval(video.timerId)
        }
        this.videoSource.splice(index, 1)
      }
    },

    /**
     * Check if video is selected
     * @param {string} id - Video ID
     * @returns {boolean} True if selected
     */
    isVideoSelected(id) {
      return this.selectedVideoIds.includes(id)
    },

    /**
     * Get the selection order number for a video
     * @param {string} id - Video ID
     * @returns {number|null} Selection order (1-based) or null if not selected
     */
    getSelectionOrder(id) {
      const index = this.selectedVideoIds.indexOf(id)
      return index >= 0 ? index + 1 : null
    },

    /**
     * Toggle video selection
     * @param {string} id - Video ID
     */
    toggleSelection(id) {
      if (!id) return

      const index = this.selectedVideoIds.indexOf(id)
      if (index > -1) {
        // Remove from selection
        this.selectedVideoIds.splice(index, 1)
      } else {
        // Add to selection
        this.selectedVideoIds.push(id)
      }
    },

    /**
     * Handle video selection
     * @param {string} videoId - Video ID
     */
    handleVideoSelect(videoId) {
      if (!videoId) return

      // Check if trying to select when limit is reached
      if (!this.isVideoSelected(videoId) && this.maxSelectionReached) {
        this.$toast.warning('Maximum 4 videos can be selected')
        return
      }

      this.toggleSelection(videoId)
    },

    /**
     * Handle next button click
     */
    handleNext() {
      const selectedVideos = this.selectedVideoIds.map(selectedId => {
        const video = this.videoSource.find(v => v.id === selectedId)
        return video || { id: selectedId }
      })
      this.$emit('next', selectedVideos)
    },

    /**
     * Clear all selected videos
     */
    clearSelection() {
      this.selectedVideoIds = []
    },

    /**
     * Cleanup all polling timers
     */
    cleanupPollingTimers() {
      this.videoSource.forEach(video => {
        if (video.timerId) {
          clearInterval(video.timerId)
        }
      })
    },

    /**
     * Handle video uploaded from AddVideoCard
     * @param {Object} video - Uploaded video object
     */
    handleVideoUploaded(video) {
      // Add to local videoSource array for display without auto-selection
      this.addVideo(video, { autoSelect: false })
    },

    /**
     * Handle videos selected from library in AddVideoCard
     * @param {Array} videos - Selected videos from library
     */
    handleVideosSelected(videos) {
      // Add each video to local videoSource array for display without auto-selection
      videos.forEach(video => {
        this.addVideo(video, { autoSelect: false })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.video-source {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
  background: #ffffff;
  padding: 24px 32px 32px;
  border-radius: 32px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.video-source__header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  margin: -24px -32px 0 -32px;
  padding-left: 32px;
  padding-right: 32px;
  background: #ffffff;
  border-radius: 32px 32px 0 0;

  .video-source__description {
    font-size: 14px;
    color: #6b7280;
  }
}

.video-source__title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1f2a4d;
}

.video-source__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.clear-selection-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: 1px solid #ef4444 !important;
  border-radius: 999px;
  background: #ffffff;
  color: #ef4444;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    background: #fef2f2;
    color: #dc2626;
    border-color: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
}

.next-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #8ec5fc 0%, #a855f7 100%);
  color: #ffffff;
  font-weight: 600;

  &:hover,
  &:focus {
    background: linear-gradient(135deg, #6ea8f9 0%, #9333ea 100%);
    color: #fff;
  }
}

</style>
